/**
 * detectTail — a tail is a TUBE that meets the body at the BACK of the PELVIS
 * (ADR-031 T1, Allen's priors, 2026-09-23: "any tail is always adjacent to the
 * BACK of the model, and always adjacent to the PELVIS/hips. It can vary a
 * lot, but it's always a TUBE.").
 *
 * WHY THIS EXISTS. The T1 detector (`unarmatured.ts`) called a cluster's shape
 * from its bounding box. Measured on the 2026-09-23 Tripo presets, that is the
 * gate that lost every tail: the five tiger presets' tails curl in one plane
 * (bbox 31 × 30 × 6 cm → flatness 0.19–0.22 → "sheet", offered as cloth), and
 * the kobold's 60 cm tail was clustered WITH the pelvis flesh it grows from
 * (bbox 61 × 50 × 39 cm → elongation 1.6 → "blob", never offered). Nothing
 * about the rig's names or facing was involved: the old detector never read a
 * name. A bounding box cannot see a tube that bends.
 *
 * THE RULE, in the order it is applied (each refusal names its gate):
 *
 *  1. THE RIG'S OWN FRAME. The leg fork is the deepest bone with two child
 *     subtrees that reach the floor, laterally apart — the pelvis on every rig
 *     in the corpus (Tripo `Pelvis`, `mixamorigHips`, `J_Bip_C_Hips`, UE5
 *     `pelvis`, Unity `Hips`), found without a name. The two children are the
 *     hip joints; their midpoint is the hip centre. Forward is measured from
 *     the feet (toe bones sit ahead of the ankle), so a rig facing ±X (the
 *     goblin) is as legible as one facing +Z. Up is glTF's +Y, the height is
 *     the mesh's own. The two children must stand side by side — neither
 *     lying part-way along the other's bone: on the 96 cm husky
 *     (ActorCore, 2026-09-24) the LEFT CALF's foot and its twist chain both
 *     reach the floor, the toe 4+ cm ahead, and the calf read as the pelvis —
 *     "behind the hips" became a plane through the shin, and the zone took
 *     the tail, the left buttock and thigh and a band of lower back (3,007
 *     faces, 47% Pelvis / 39% L_Thigh). The twist chain lies ON the shin.
 *  2. SEEDS — flesh the rig does not explain. A vertex no bone segment
 *     reaches (the T1 measurement, 2× the median), OR a vertex the pelvis
 *     (the fork or an ancestor) owns by dominant weight that sits behind the
 *     hips' coronal plane. The second cue is the census's (PR #184): every
 *     2026-09-23 preset paints its tail 100% to the pelvis, and the legs to
 *     the thighs — so a tail hanging against the legs still seeds apart from
 *     them. Native Tripo paints its tail to the thigh twists instead (the
 *     tiger warrior); there the first cue carries it.
 *  3. CLUSTERS by mesh connectivity (position-welded), grown two rings.
 *  4. THE TUBE WALK. From the cluster's far end (the farthest point from where
 *     it meets the body, geodesically) the flesh is cut into geodesic slices.
 *     Walking back toward the body, a slice is accepted while it is ONE piece,
 *     roughly round, and no fatter than 1.6× the slices just behind it; two
 *     bad slices in a row end the tube. The walk stops where the tube meets
 *     the body — the section splits (two cheeks, two legs), balloons (the
 *     buttock), or REBOUNDS: a bushy tail swells past its middle and narrows
 *     at its dock, and past that neck (≤ 0.75× the tail's widest slice),
 *     flesh widening again beyond 1.25× the neck is the rump, however gently
 *     it widens. That is what separates a tail from the pelvis it grows out of.
 *     Wider grows rings of faces toward the body; TIGHTER (negative rings)
 *     sheds them from the tube's body side, never below half the walked tube.
 *     Where the rig painted the walked tube to the pelvis, flesh inside it the
 *     THIGHS own is leg caught at the junction and is dropped (the owner cue;
 *     measured 5–60 welded vertices on the presets, and it takes the tiger
 *     presets' outside-the-tube count at T2 from 8–17 to 0–2).
 *  5. GATES on the walked tube:
 *     - `anchor`  — its root lies within 25% of the body's height of the hip
 *                   centre (tails measured 7–13%; nearest non-tail 19%).
 *     - `back`    — its root is behind the hips' coronal plane.
 *     - `length`  — at least 15% of the height (tails 38–98%).
 *     - `slender` — length ≥ 6× its median radius (tails 12–30; the
 *                   squirrel's stub and the buttocks read 2–3).
 *     - `round`   — median slice roundness ≥ 0.5 (tails 0.79–0.89).
 *
 * READ THE BODY AS THE FILE PAINTED IT. The owner cues read `input.skin`; a
 * Document run re-owns the buttock and lower back to the pelvis (ADR-029)
 * before the zones panel measures, and fed that paint the pelvis cue seeded
 * the lower back and the kobold's zone climbed 15 cm up it (2026-09-23: 117 cm,
 * root above the hips, against 94 cm at the tail's base). The app feeds the
 * load door's record (adapter `subjectArrays(scene, { paint: 'file' })`, the
 * app's `tailDetectArrays`); `app/src/viewport/tail-app-path.test.ts` holds
 * the app's door and the file's to the same zone.
 *
 * It proposes; it never decides — the flyout's "this is a tail" and the
 * declared zone stay the override (ADR-031 §9.3). A candidate it refuses is
 * reported WITH the gate that refused it, never dropped silently.
 *
 * Kernel-pure: typed arrays in, plain data out. Depends on: zones/unarmatured
 * (the input shape and the segment distance). Depended on by: the app's zone
 * proposer and the tail specimen probes.
 */
import { nearestSegmentDistances, type DetectorBone, type Point3, type UnarmaturedInput } from './unarmatured.js';
import { prominentTips } from './prominent-tips.js';
import { tailCentringYaw } from '../tails/recentre.js';

/** Measured thresholds, as fractions of the body's height unless named otherwise (see the module header). */
export const TAIL_DETECT_LIMITS = {
  /** Root within this fraction of the height from the hip centre. */
  anchorRadius: 0.25,
  /** Tube length at least this fraction of the height. */
  minLength: 0.15,
  /** Tube length over its median radius. */
  minSlenderness: 6,
  /**
   * …and for a BUSHY tail — one that swells and narrows to its dock — whose
   * median radius is the brush; and for one of a FAN, whose walk runs from its
   * tip into the fused base of the others (the three-tail kitsune: 5.3).
   */
  minBushySlenderness: 4,
  /** Median slice roundness (√(λ2/λ1) of the slice's spread). */
  minRoundness: 0.5,
  /** Seed multiplier on the median nearest-segment distance (the T1 default). */
  seedMultiplier: 2,
  /** A slice fatter than this × the recent slices is the body, not the tube. */
  balloon: 1.6,
  /** A possible stub is at least this long… */
  minStubLength: 0.03,
  /** …its flesh sits within this of the midline (fraction of the height)… */
  stubMidline: 0.04,
  /** …and it points out behind: its backward protrusion is at least this share of its length. */
  stubOutward: 0.4,
  /** Slices below this roundness read flat — unless they close round (a paddle's). */
  flatRoundness: 0.3,
  /** A flattened tube: sections at least this round (aspect ≤ 1 / it)… */
  minPaddleRoundness: 0.15,
  /** …closing round their centre on at least this share of the walked slices. */
  paddleClosed: 0.7,
  /** A tube that narrows to this fraction of its widest slice has a NECK (a bushy tail's dock)… */
  neck: 0.75,
  /** …and past the neck a slice wider than this × the neck is the body again. */
  rebound: 1.25,
} as const;

export type TailGate = 'anchor' | 'back' | 'length' | 'slender' | 'round';

export interface TailRigFrame {
  /** The deepest bone whose two child subtrees reach the floor apart — the pelvis, found by structure. */
  readonly legFork: string;
  /** The fork's two leg children (the hip joints). */
  readonly hipJoints: readonly [string, string];
  readonly hipCentre: Point3;
  /** Unit, horizontal, measured from the feet. */
  readonly forward: Point3;
  /** The mesh's vertical extent — every threshold scales by it. */
  readonly height: number;
  /** How far the toes sit ahead of the ankles, summed over both feet (world units) — the facing's evidence. */
  readonly facingEvidence: number;
  /** The fork and its ancestors: the bones whose flesh is "the pelvis". */
  readonly pelvisBones: readonly string[];
}

export interface TailCandidate {
  /** The walked tube's vertices (every position-welded twin included). */
  readonly vertexIndices: Uint32Array;
  /** Faces whose three corners are all in the tube — the zone's face set. */
  readonly faceIndices: Uint32Array;
  /** Welded vertices in the seed cluster before the walk trimmed it. */
  readonly clusterVertices: number;
  /** How many of the cluster's seeds each cue contributed (a seed can carry both). */
  readonly seeds: { readonly unarmatured: number; readonly pelvisOwned: number };
  readonly tip: Point3;
  /** Centroid of the last accepted slice — where the tube meets the body. */
  readonly root: Point3;
  /** Geodesic length tip → root (world units). */
  readonly length: number;
  readonly medianRadius: number;
  /** length / medianRadius. */
  readonly slenderness: number;
  readonly roundness: number;
  /** |root − hip centre| / height. */
  readonly rootDistance: number;
  /** (root − hip centre)·forward / height — negative is behind. */
  readonly rootForward: number;
  /** Slice radii tip → root (world units) — the taper, as evidence. */
  readonly radii: readonly number[];
  /** Welded vertices the owner cue dropped from a pelvis-painted tube because the thighs own them. */
  readonly legTrimmed: number;
  /**
   * The rings actually applied: `growRings` itself, or, shedding, as many as
   * the tube could lose before it would be under half its walked size.
   */
  readonly rings: number;
  /** Welded vertices EXTEND TIPS took beyond the seam (0 when not asked, or nothing was left to take). */
  readonly tipsTaken: number;
  /** Rings the seed cluster was grown by before the walk (more when the seeds ran out before the tube did). */
  readonly clusterRings: number;
  /** The walk passed a neck — a swell narrowing to the dock (the `rebound` profile). */
  readonly bushy: boolean;
  /**
   * How many tails leave this junction (1 = one tail). More is a FAN — a
   * kitsune — whose zone is every tail at once: T2 refuses one chain across
   * it, and a fan is never re-centred (the spread is the design).
   */
  readonly fan: number;
  /**
   * `tube` — a confident tail (a tube, a fan, a paddle). `stub` — refused as a
   * tube for length or thickness, but single, near the midline, behind the
   * hips and protruding past the back line: a POSSIBLE stub tail (a rabbit's
   * puff, a squirrel's nub), offered at a lower confidence tier.
   */
  readonly kind: 'tube' | 'stub';
  /**
   * A FLATTENED tube (a beaver's paddle): its aspect (width over thickness)
   * and its thin axis, world, pointing up — the axis it bends across. Null for
   * a round tail.
   */
  readonly flat: { readonly aspect: number; readonly thinAxis: Point3 } | null;
  /** How far the tail sits off the body's centre line (the illustrator's sideways sweep). */
  readonly centring: TailCentring;
  /** Why the walk stopped: `junction` (it ran out of cluster) or the flags of the slices that ended it (`rebound`: flesh widening again past a bushy tail's neck). */
  readonly stop: string;
  readonly dominantBones: readonly { readonly bone: string; readonly share: number }[];
  /** The first gate that refused it, or null for a tail. */
  readonly refusedBy: TailGate | null;
  /** The refusal as a sentence a person can read, or null. */
  readonly reason: string | null;
}

export interface TailCentring {
  /** Angle between root → the tail's centroid and the body's mid-sagittal plane (degrees, 0 = centred behind). */
  readonly angleDeg: number;
  /** Which side of the character it leans to (the CHARACTER's left or right). */
  readonly side: 'left' | 'right' | 'centre';
  /** The root's own sideways offset from the hip centre (world units, + = the character's left). */
  readonly rootOffset: number;
  /**
   * The turn about world +Y through the root that brings the tail's PROXIMAL
   * direction (root → a third of the way down) to straight back — what "centre
   * the tail" applies (tails/recentre). Degrees; its droop is left alone.
   */
  readonly yawDeg: number;
}

export interface TailDetectReport {
  /** The rig's measured frame, or null when it could not be measured (see `frameRefusal`). */
  readonly frame: TailRigFrame | null;
  readonly frameRefusal: string | null;
  /** Every candidate, accepted first, then nearest the hips first. */
  readonly candidates: readonly TailCandidate[];
  /** The accepted candidate (the longest if several), or null. */
  readonly tail: TailCandidate | null;
  /**
   * When there is no confident tail: the possible STUB nearest the hips, as a
   * candidate a person may pin (lower confidence; never auto-grown), or null.
   */
  readonly stub: TailCandidate | null;
  /** One line: the tail, or why there is none — never silent. */
  readonly summary: string;
}

export interface TailDetectOptions {
  /**
   * Rings of faces to add to an accepted tube toward the body (the flyout's
   * wider), or, negative, to shed from its body side (tighter). Default 0.
   */
  readonly growRings?: number;
  /**
   * EXTEND TIPS (Allen, 2026-09-25, Kitty): take every piece of the tail's
   * cluster BEYOND the zone's seam — a fan's other tails — and nothing nearer
   * the body. Wider's rings grow only toward the body (EXTEND STEM); before,
   * one ring grew both ways at once, into the hips and partway up the fan.
   */
  readonly extendTips?: boolean;
  /** Clusters with fewer welded vertices are noise. Default 30. */
  readonly minClusterVertices?: number;
}

const CLUSTER_RINGS = 2;
/** A walk that ran out of cluster is re-walked on a cluster this many rings wider, up to the max. */
const EXTEND_STEP = 4;
const CLUSTER_RINGS_MAX = 30;


/**
 * Does `p` lie on the bone `from → to`, part-way along it (10–100% of its
 * length, within 15% of it sideways)? A twist bone does; a sibling hip joint,
 * or a zero-length helper sitting at the fork itself, does not.
 */
function onSegment(from: Point3, to: Point3, p: Point3): boolean {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const L2 = dx * dx + dy * dy + dz * dz;
  if (!(L2 > 1e-12)) return false;
  const t = ((p.x - from.x) * dx + (p.y - from.y) * dy + (p.z - from.z) * dz) / L2;
  if (t < 0.1 || t > 1) return false;
  const off = Math.hypot(p.x - from.x - t * dx, p.y - from.y - t * dy, p.z - from.z - t * dz);
  return off < 0.15 * Math.sqrt(L2);
}
/** Tighter never sheds a walked tube below this fraction of its vertices — past it is the tail itself. */
const TIGHTEST_KEEP = 0.5;
const SLICES = 40;

// ---------------------------------------------------------------- the rig ----

function measureFrame(bones: readonly DetectorBone[], height: number): TailRigFrame | string {
  const byName = new Map(bones.map((b) => [b.name, b]));
  const children = new Map<string, string[]>();
  for (const b of bones) {
    if (b.parent === null || !byName.has(b.parent)) continue;
    const list = children.get(b.parent) ?? [];
    list.push(b.name);
    children.set(b.parent, list);
  }
  const kids = (n: string): readonly string[] => children.get(n) ?? [];
  let floor = Infinity;
  for (const b of bones) floor = Math.min(floor, b.worldPosition.y);
  const lowest = new Map<string, Point3>();
  const lowestOf = (n: string): Point3 => {
    const seen = lowest.get(n);
    if (seen !== undefined) return seen;
    let best = byName.get(n)!.worldPosition;
    for (const c of kids(n)) {
      const l = lowestOf(c);
      if (l.y < best.y) best = l;
    }
    lowest.set(n, best);
    return best;
  };
  const depth = (n: string): number => {
    let d = 0;
    let at = byName.get(n)!.parent;
    while (at !== null && byName.has(at)) {
      d++;
      at = byName.get(at)!.parent;
    }
    return d;
  };
  let fork: { name: string; depth: number; legs: [string, string] } | null = null;
  for (const b of bones) {
    const reach = kids(b.name)
      .map((c) => ({ c, low: lowestOf(c) }))
      .filter((r) => r.low.y < floor + 0.06 * height);
    if (reach.length < 2) continue;
    let pair: [string, string] | null = null;
    let apart = 0.04 * height;
    for (let i = 0; i < reach.length; i++) {
      for (let j = i + 1; j < reach.length; j++) {
        // Two HIP JOINTS stand side by side. A calf's foot and its twist
        // chain also both reach the floor on a short body (the husky, 96 cm:
        // CalfTwist02 sits at the ankle, the toe 4+ cm ahead) — but a twist
        // bone lies ON its sibling's bone, fork → child, part-way along it:
        // one leg, stacked, not two.
        const at = byName.get(b.name)!.worldPosition;
        const pa = byName.get(reach[i]!.c)!.worldPosition;
        const pb = byName.get(reach[j]!.c)!.worldPosition;
        if (onSegment(at, pa, pb) || onSegment(at, pb, pa)) continue;
        const d = Math.hypot(reach[i]!.low.x - reach[j]!.low.x, reach[i]!.low.z - reach[j]!.low.z);
        if (d > apart) {
          apart = d;
          pair = [reach[i]!.c, reach[j]!.c];
        }
      }
    }
    if (pair === null) continue;
    const d = depth(b.name);
    if (fork === null || d > fork.depth) fork = { name: b.name, depth: d, legs: pair };
  }
  if (fork === null) {
    return 'no bone forks into two legs that reach the floor — without the pelvis there is no "behind the hips" to anchor a tail';
  }
  const a = byName.get(fork.legs[0])!.worldPosition;
  const b = byName.get(fork.legs[1])!.worldPosition;
  const hipCentre = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: (a.z + b.z) / 2 };
  const lx = b.x - a.x;
  const lz = b.z - a.z;
  const ll = Math.hypot(lx, lz);
  if (ll < 1e-9) return `the hip joints ${fork.legs[0]} and ${fork.legs[1]} coincide — no lateral axis to measure facing from`;
  // forward = lateral × up (up = +Y), sign fixed by the feet below.
  let fx = -lz / ll;
  let fz = lx / ll;
  let evidence = 0;
  const low = floor + 0.15 * height;
  for (const leg of fork.legs) {
    const stack = [leg];
    while (stack.length > 0) {
      const n = stack.pop()!;
      stack.push(...kids(n));
      const bone = byName.get(n)!;
      const parent = bone.parent === null ? undefined : byName.get(bone.parent);
      // The ANKLE: the first bone of this leg inside the foot band. Its
      // descendants inside the band are the foot's forward bones.
      if (!(bone.worldPosition.y < low) || (parent !== undefined && parent.worldPosition.y < low)) continue;
      const toes: Point3[] = [];
      const inner = [...kids(n)];
      while (inner.length > 0) {
        const m = inner.pop()!;
        inner.push(...kids(m));
        const p = byName.get(m)!.worldPosition;
        if (p.y < low) toes.push(p);
      }
      if (toes.length === 0) continue;
      const tx = toes.reduce((s, p) => s + p.x, 0) / toes.length - bone.worldPosition.x;
      const tz = toes.reduce((s, p) => s + p.z, 0) / toes.length - bone.worldPosition.z;
      evidence += tx * fx + tz * fz;
    }
  }
  if (Math.abs(evidence) < 0.005 * height) {
    // HOOVES (Allen, 2026-09-25, ponyBoy: "no tail detection on the horse
    // tail"): a hoof's toe joint sits ON the ankle, so the feet cannot sign the
    // lateral axis. The hip joints' own left/right NAMES can: forward =
    // (left − right) × up. Without names either way, the refusal stands.
    const side = (n: string): 'L' | 'R' | null =>
      /(^|[:_ .])(l|left)[_ .]?[a-z]|[_ .]l$/i.test(n) && !/(^|[:_ .])(r|right)[_ .]?[a-z]|[_ .]r$/i.test(n)
        ? 'L'
        : /(^|[:_ .])(r|right)[_ .]?[a-z]|[_ .]r$/i.test(n)
          ? 'R'
          : null;
    const sa = side(fork.legs[0]);
    const sb = side(fork.legs[1]);
    if (!((sa === 'L' && sb === 'R') || (sa === 'R' && sb === 'L'))) {
      return `the feet carry no toe bones ahead of the ankles (${(evidence * 100).toFixed(1)} cm) and the hip joints name no left and right — the rig's facing cannot be measured, so "behind the hips" cannot be either`;
    }
    // lateral runs a → b; with a = left, a +Z-facing body's lateral points −X and (−lz, lx) points −Z: flip.
    if (sa === 'L') {
      fx = -fx;
      fz = -fz;
    }
    evidence = 0;
  } else if (evidence < 0) {
    fx = -fx;
    fz = -fz;
  }
  const pelvisBones = [fork.name];
  let up = byName.get(fork.name)!.parent;
  while (up !== null && byName.has(up)) {
    pelvisBones.push(up);
    up = byName.get(up)!.parent;
  }
  return {
    legFork: fork.name,
    hipJoints: fork.legs,
    hipCentre,
    forward: { x: fx, y: 0, z: fz },
    height,
    facingEvidence: Math.abs(evidence),
    pelvisBones,
  };
}

// ------------------------------------------------------- graph machinery ----

/** A binary min-heap of (key, node) — Dijkstra's queue. */
class MinHeap {
  private keys: number[] = [];
  private nodes: number[] = [];
  get size(): number {
    return this.keys.length;
  }
  push(key: number, node: number): void {
    const k = this.keys;
    const n = this.nodes;
    let i = k.length;
    k.push(key);
    n.push(node);
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (k[p]! <= key) break;
      k[i] = k[p]!;
      n[i] = n[p]!;
      i = p;
    }
    k[i] = key;
    n[i] = node;
  }
  pop(): [number, number] {
    const k = this.keys;
    const n = this.nodes;
    const top: [number, number] = [k[0]!, n[0]!];
    const lastK = k.pop()!;
    const lastN = n.pop()!;
    if (k.length > 0) {
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        if (l >= k.length) break;
        const r = l + 1;
        const c = r < k.length && k[r]! < k[l]! ? r : l;
        if (k[c]! >= lastK) break;
        k[i] = k[c]!;
        n[i] = n[c]!;
        i = c;
      }
      k[i] = lastK;
      n[i] = lastN;
    }
    return top;
  }
}

interface Mesh {
  readonly P: Float32Array;
  /** Welded neighbours, by representative. */
  readonly adj: Map<number, number[]>;
  readonly rep: Int32Array;
  readonly twins: Map<number, number[]>;
}

const dist3 = (P: Float32Array, a: number, b: number): number =>
  Math.hypot(P[a * 3]! - P[b * 3]!, P[a * 3 + 1]! - P[b * 3 + 1]!, P[a * 3 + 2]! - P[b * 3 + 2]!);

function geodesic(m: Mesh, sources: readonly number[], inside: Set<number>): Map<number, number> {
  const d = new Map<number, number>();
  const heap = new MinHeap();
  for (const s of sources) {
    d.set(s, 0);
    heap.push(0, s);
  }
  while (heap.size > 0) {
    const [du, u] = heap.pop();
    if (du > d.get(u)!) continue;
    for (const v of m.adj.get(u) ?? []) {
      if (!inside.has(v)) continue;
      const nd = du + dist3(m.P, u, v);
      if (nd < (d.get(v) ?? Infinity)) {
        d.set(v, nd);
        heap.push(nd, v);
      }
    }
  }
  return d;
}

/** Connected pieces of `nodes` over the welded adjacency, largest first. */
function pieces(m: Mesh, nodes: Iterable<number>): number[][] {
  const set = new Set(nodes);
  const seen = new Set<number>();
  const out: number[][] = [];
  for (const s of set) {
    if (seen.has(s)) continue;
    seen.add(s);
    const piece: number[] = [];
    const stack = [s];
    while (stack.length > 0) {
      const u = stack.pop()!;
      piece.push(u);
      for (const v of m.adj.get(u) ?? []) {
        if (set.has(v) && !seen.has(v)) {
          seen.add(v);
          stack.push(v);
        }
      }
    }
    out.push(piece);
  }
  return out.sort((a, b) => b.length - a.length);
}

/** Eigenvalues of a symmetric 3×3 (xx yy zz xy xz yz), descending — the trigonometric closed form. */
function eigenvalues3(c: readonly number[]): [number, number, number] {
  const [a, b, d, e, f, g] = c as [number, number, number, number, number, number];
  const p1 = e * e + f * f + g * g;
  if (p1 < 1e-30) return [a, b, d].sort((x, y) => y - x) as [number, number, number];
  const q = (a + b + d) / 3;
  const p2 = (a - q) ** 2 + (b - q) ** 2 + (d - q) ** 2 + 2 * p1;
  const p = Math.sqrt(p2 / 6);
  const B = [(a - q) / p, (b - q) / p, (d - q) / p, e / p, f / p, g / p] as const;
  const detB = B[0] * (B[1] * B[2] - B[5] * B[5]) - B[3] * (B[3] * B[2] - B[5] * B[4]) + B[4] * (B[3] * B[5] - B[1] * B[4]);
  const r = Math.max(-1, Math.min(1, detB / 2));
  const phi = Math.acos(r) / 3;
  const e1 = q + 2 * p * Math.cos(phi);
  const e3 = q + 2 * p * Math.cos(phi + (2 * Math.PI) / 3);
  return [e1, 3 * q - e1 - e3, e3];
}

interface Slice {
  readonly hi: number;
  readonly centre: Point3;
  readonly radius: number;
  readonly roundness: number;
  readonly pieces: number;
  /**
   * The section goes all the way round its centre (a tube's, however flat),
   * not a curve that stops (a panel's): ≥ 7 of 8 angular sectors about the
   * walk's axis hold flesh. Set by the post-pass once the neighbours are known.
   */
  closed: boolean;
  /** The section's thin direction (unit, world) when it is flattened — the paddle's normal. */
  thin: Point3 | null;
}

function slices(m: Mesh, members: readonly number[], fromTip: Map<number, number>, delta: number): Slice[] {
  const bins = new Map<number, number[]>();
  for (const v of members) {
    const k = Math.floor(fromTip.get(v)! / delta);
    const list = bins.get(k) ?? [];
    list.push(v);
    bins.set(k, list);
  }
  const P = m.P;
  const out: Slice[] = [];
  for (const k of [...bins.keys()].sort((a, b) => a - b)) {
    const vs = bins.get(k)!;
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (const v of vs) {
      cx += P[v * 3]!;
      cy += P[v * 3 + 1]!;
      cz += P[v * 3 + 2]!;
    }
    cx /= vs.length;
    cy /= vs.length;
    cz /= vs.length;
    let r = 0;
    const cov = [0, 0, 0, 0, 0, 0];
    for (const v of vs) {
      const dx = P[v * 3]! - cx;
      const dy = P[v * 3 + 1]! - cy;
      const dz = P[v * 3 + 2]! - cz;
      r += Math.hypot(dx, dy, dz);
      cov[0]! += dx * dx;
      cov[1]! += dy * dy;
      cov[2]! += dz * dz;
      cov[3]! += dx * dy;
      cov[4]! += dx * dz;
      cov[5]! += dy * dz;
    }
    r /= vs.length;
    const [l1, l2] = eigenvalues3(cov);
    const roundness = vs.length > 3 && l1 > 0 ? Math.sqrt(Math.max(0, l2) / l1) : 0;
    // One piece? Counted over the band of this slice and the next, so a thin
    // slice on a coarse mesh is not "split" by the triangulation.
    const mine = new Set(vs);
    const band = [...vs, ...(bins.get(k + 1) ?? [])];
    const need = Math.max(3, 0.15 * vs.length);
    let count = 0;
    for (const piece of pieces(m, band)) {
      let own = 0;
      for (const u of piece) if (mine.has(u)) own++;
      if (own >= need) count++;
    }
    out.push({ hi: (k + 1) * delta, centre: { x: cx, y: cy, z: cz }, radius: r, roundness, pieces: count, closed: false, thin: null });
  }
  // THE SECTION'S SHAPE about the walk's own axis (the neighbouring centres):
  // does it close round, and which way is it thin? A beaver's paddle is a
  // FLATTENED tube (Bonnie, 2026-09-24: roundness 0.37, refused as a panel) —
  // closed, one piece, thin in one direction.
  const keys = [...bins.keys()].sort((a, b) => a - b);
  out.forEach((sl, i) => {
    const a = out[Math.max(0, i - 1)]!.centre;
    const b = out[Math.min(out.length - 1, i + 1)]!.centre;
    let ax = b.x - a.x;
    let ay = b.y - a.y;
    let az = b.z - a.z;
    const al = Math.hypot(ax, ay, az);
    if (!(al > 1e-12)) return;
    ax /= al;
    ay /= al;
    az /= al;
    // A basis of the section plane.
    const [rx, ry, rz] = Math.abs(ay) < 0.9 ? [0, 1, 0] : [1, 0, 0];
    let ux = ay * rz - az * ry;
    let uy = az * rx - ax * rz;
    let uz = ax * ry - ay * rx;
    const ul = Math.hypot(ux, uy, uz);
    ux /= ul;
    uy /= ul;
    uz /= ul;
    const vx = ay * uz - az * uy;
    const vy = az * ux - ax * uz;
    const vz = ax * uy - ay * ux;
    const vs = bins.get(keys[i]!)!;
    const pu: number[] = [];
    const pv: number[] = [];
    let suu = 0;
    let svv = 0;
    let suv = 0;
    for (const v of vs) {
      const dx = P[v * 3]! - sl.centre.x;
      const dy = P[v * 3 + 1]! - sl.centre.y;
      const dz = P[v * 3 + 2]! - sl.centre.z;
      const a1 = dx * ux + dy * uy + dz * uz;
      const a2 = dx * vx + dy * vy + dz * vz;
      pu.push(a1);
      pv.push(a2);
      suu += a1 * a1;
      svv += a2 * a2;
      suv += a1 * a2;
    }
    // Principal axes of the section: major at `phi`, minor at phi + 90°.
    const phi = 0.5 * Math.atan2(2 * suv, suu - svv);
    const cp = Math.cos(phi);
    const sp = Math.sin(phi);
    let s1 = 0;
    let s2 = 0;
    for (let q = 0; q < pu.length; q++) {
      const m1 = pu[q]! * cp + pv[q]! * sp;
      const m2 = -pu[q]! * sp + pv[q]! * cp;
      s1 += m1 * m1;
      s2 += m2 * m2;
    }
    // Whitened: a flattened ring becomes a circle and fills every sector; an
    // open curve stays an arc. A section with no thickness at all is no tube.
    const sectors = new Uint8Array(8);
    if (s2 > 1e-6 * s1 && s1 > 0) {
      const k1 = Math.sqrt(s1);
      const k2 = Math.sqrt(s2);
      for (let q = 0; q < pu.length; q++) {
        const m1 = (pu[q]! * cp + pv[q]! * sp) / k1;
        const m2 = (-pu[q]! * sp + pv[q]! * cp) / k2;
        sectors[Math.min(7, Math.floor(((Math.atan2(m2, m1) + Math.PI) / (2 * Math.PI)) * 8))] = 1;
      }
    }
    sl.closed = sectors.reduce((n, x) => n + x, 0) >= 7;
    const c = -sp;
    const sn = cp;
    sl.thin = { x: c * ux + sn * vx, y: c * uy + sn * vy, z: c * uz + sn * vz };
  });
  return out;
}

function walk(sl: readonly Slice[]): { accepted: Slice[]; stop: string; necked: boolean } {
  const accepted: Slice[] = [];
  let bad = 0;
  // The tail's OWN profile: its widest slice so far (past the tip's cap), and the narrowest since.
  let peak = 0;
  let neck = Infinity;
  // The TIP CAP: from its point a tail widens slice after slice, and a bushy
  // one blooms fast (the wolf: 1.1 → 2.2 → 3.8 → 4.9 cm, a 30° cone) — which
  // is no balloon. "Fatter than the slices behind it" is judged only once the
  // tube has a body: the first slice no wider than the one before it.
  let capped = true;
  for (let i = 0; i < sl.length; i++) {
    const s = sl[i]!;
    if (i < 2) {
      // The tip's first slices are a cap, not the profile (the tiger's reads 2.1 cm, then 1.5).
      accepted.push(s);
      continue;
    }
    const recent = accepted.slice(-4).map((a) => a.radius).sort((a, b) => a - b);
    const ref = recent[Math.floor(recent.length / 2)]!;
    const flags: string[] = [];
    if (s.pieces >= 2) flags.push('split');
    if (capped && !(s.radius > accepted[accepted.length - 1]!.radius)) capped = false;
    if (!capped && s.radius > TAIL_DETECT_LIMITS.balloon * ref && s.radius - ref > 0.01) flags.push('balloon');
    // A BUSHY tail swells past its middle and narrows again at its dock — the
    // husky, 4.2 cm at the brush, 2.6 cm where it leaves the body. Past that
    // neck, flesh that widens again is the rump the tail grows from, however
    // gently it widens (the husky's buttock climbed 2.5 → 2.9 → 4.2 → 5.3 cm,
    // never 1.6× the slices behind it). A tail with no neck (the kobold's
    // reptile taper, the tigers' rope) never sets this.
    if (neck <= TAIL_DETECT_LIMITS.neck * peak && s.radius > TAIL_DETECT_LIMITS.rebound * neck && s.radius - neck > 0.005) flags.push('rebound');
    // Flat AND open is a panel's edge; a closed flat section is a paddle's.
    if (s.roundness < TAIL_DETECT_LIMITS.flatRoundness && !(s.closed && s.roundness >= TAIL_DETECT_LIMITS.minPaddleRoundness)) flags.push('flat');
    if (flags.length > 0) {
      bad++;
      if (bad >= 2) {
        accepted.pop();
        return { accepted, stop: flags.join('+'), necked: neck <= TAIL_DETECT_LIMITS.neck * peak };
      }
    } else {
      bad = 0;
    }
    accepted.push(s);
    if (flags.length === 0) {
      if (s.radius >= peak) {
        peak = s.radius;
        neck = Infinity;
      } else {
        neck = Math.min(neck, s.radius);
      }
    }
  }
  return { accepted, stop: 'junction', necked: neck <= TAIL_DETECT_LIMITS.neck * peak };
}

// ------------------------------------------------------------ the detector ----

export function detectTail(input: UnarmaturedInput, options: TailDetectOptions = {}): TailDetectReport {
  const growRings = options.growRings ?? 0;
  const minCluster = options.minClusterVertices ?? 30;
  const P = input.positions;
  const n = P.length / 3;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < n; i++) {
    const y = P[i * 3 + 1]!;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const height = maxY - minY;
  const none = (frameRefusal: string, frame: TailRigFrame | null = null): TailDetectReport => ({
    frame,
    frameRefusal,
    candidates: [],
    tail: null,
    stub: null,
    summary: `no tail: ${frameRefusal}`,
  });
  if (n === 0 || input.indices.length < 3 || !(height > 0)) return none('the mesh has no extent to measure');
  const measured = measureFrame(input.bones, height);
  if (typeof measured === 'string') return none(measured);
  const frame = measured;
  const C = frame.hipCentre;
  const F = frame.forward;
  const forwardOf = (i: number): number => (P[i * 3]! - C.x) * F.x + (P[i * 3 + 2]! - C.z) * F.z;

  // ---- weld + adjacency ----
  const rep = new Int32Array(n);
  const byKey = new Map<string, number>();
  const twins = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const k = `${P[i * 3]},${P[i * 3 + 1]},${P[i * 3 + 2]}`;
    let r = byKey.get(k);
    if (r === undefined) {
      r = i;
      byKey.set(k, i);
    }
    rep[i] = r;
    const list = twins.get(r) ?? [];
    list.push(i);
    twins.set(r, list);
  }
  const adjSets = new Map<number, Set<number>>();
  const link = (a: number, b: number): void => {
    if (a === b) return;
    let s = adjSets.get(a);
    if (s === undefined) {
      s = new Set();
      adjSets.set(a, s);
    }
    s.add(b);
  };
  const faceCount = Math.floor(input.indices.length / 3);
  for (let f = 0; f < faceCount; f++) {
    const a = rep[input.indices[f * 3]!]!;
    const b = rep[input.indices[f * 3 + 1]!]!;
    const c = rep[input.indices[f * 3 + 2]!]!;
    link(a, b);
    link(b, a);
    link(b, c);
    link(c, b);
    link(c, a);
    link(a, c);
  }
  const adj = new Map<number, number[]>();
  for (const [k, s] of adjSets) adj.set(k, [...s]);
  const mesh: Mesh = { P, adj, rep, twins };

  // ---- seeds ----
  const segDist = nearestSegmentDistances(P, input.bones);
  const sorted = Float32Array.from(segDist).sort();
  const threshold = sorted[Math.floor(n / 2)]! * TAIL_DETECT_LIMITS.seedMultiplier;
  const pelvisIdx = new Set<number>();
  input.bones.forEach((b, i) => {
    if (frame.pelvisBones.includes(b.name)) pelvisIdx.add(i);
  });
  const dominant = (i: number): number => {
    if (!input.skin) return -1;
    let bw = 0;
    let bb = -1;
    for (let k = 0; k < 4; k++) {
      const w = input.skin.weight[i * 4 + k]!;
      if (w > bw) {
        bw = w;
        bb = input.skin.index[i * 4 + k]!;
      }
    }
    return bb;
  };
  // The legs: every bone under either hip joint.
  const legNames = new Set<string>();
  {
    const stack = [...frame.hipJoints];
    while (stack.length > 0) {
      const b = stack.pop()!;
      legNames.add(b);
      for (const c of input.bones) if (c.parent === b) stack.push(c.name);
    }
  }
  const legIdx = new Set<number>();
  input.bones.forEach((b, i) => {
    if (legNames.has(b.name)) legIdx.add(i);
  });
  const byUnarmatured = new Set<number>();
  const byPelvis = new Set<number>();
  for (const r of twins.keys()) {
    if (segDist[r]! > threshold) byUnarmatured.add(r);
    if (pelvisIdx.has(dominant(r)) && forwardOf(r) < 0) byPelvis.add(r);
  }
  const seeds = new Set([...byUnarmatured, ...byPelvis]);

  // ---- clusters → walked tubes → gates ----
  const candidates: TailCandidate[] = [];
  const measure = (seedPiece: readonly number[], clusterRings: number): { c: TailCandidate; walked: Set<number>; cluster: Set<number>; sources: readonly number[] } => {
    const cluster = new Set(seedPiece);
    let frontier = seedPiece;
    for (let ring = 0; ring < clusterRings; ring++) {
      const next: number[] = [];
      for (const u of frontier) {
        for (const v of adj.get(u) ?? []) {
          if (!cluster.has(v)) {
            cluster.add(v);
            next.push(v);
          }
        }
      }
      frontier = next;
    }
    const members = [...cluster];
    const junction = members.filter((u) => (adj.get(u) ?? []).some((v) => !cluster.has(v)));
    let sources = junction;
    if (sources.length === 0) {
      // A detached shell: it meets the body nowhere, so its far end is measured from its point nearest the hips.
      let best = members[0]!;
      let bd = Infinity;
      for (const u of members) {
        const d = Math.hypot(P[u * 3]! - C.x, P[u * 3 + 1]! - C.y, P[u * 3 + 2]! - C.z);
        if (d < bd) {
          bd = d;
          best = u;
        }
      }
      sources = [best];
    }
    return { ...walkTube(seedPiece, cluster, sources, clusterRings), cluster, sources };
  };
  /** Walk ONE tube over `cluster`, measured from `sources` (where it meets the body): the candidate, and the walked vertices. */
  const walkTube = (
    seedPiece: readonly number[],
    cluster: Set<number>,
    sources: readonly number[],
    clusterRings: number,
  ): { c: TailCandidate; walked: Set<number> } => {
    const members = [...cluster];
    const fromBody = geodesic(mesh, sources, cluster);
    let tip = members[0]!;
    let far = -1;
    for (const [u, d] of fromBody) {
      if (d > far) {
        far = d;
        tip = u;
      }
    }
    // A FAN: how many tails leave this junction — the far, prominent maxima
    // of the distance from the body (zones/prominent-tips). The kitsune's
    // five fuse near the base, so one walk swallows all of them.
    const fanField = members.map((u) => fromBody.get(u) ?? Infinity);
    const at = new Map(members.map((u, k) => [u, k]));
    const fan = Math.max(
      1,
      prominentTips(fanField, (k) => (adj.get(members[k]!) ?? []).map((v) => at.get(v)).filter((q): q is number => q !== undefined)).tips.length,
    );
    const fromTip = geodesic(mesh, [tip], cluster);
    const reached = members.filter((u) => fromTip.has(u));
    let total = 0;
    for (const d of fromTip.values()) total = Math.max(total, d);
    const edges: number[] = [];
    for (const u of reached) for (const v of adj.get(u) ?? []) if (v > u && cluster.has(v)) edges.push(dist3(P, u, v));
    edges.sort((a, b) => a - b);
    const edge = edges.length > 0 ? edges[Math.floor(edges.length / 2)]! : 0;
    const delta = Math.max(2 * edge, total / SLICES, 1e-9);
    const { accepted, stop, necked } = walk(slices(mesh, reached, fromTip, delta));
    const last = accepted[accepted.length - 1]!;
    const tube = new Set(reached.filter((u) => fromTip.get(u)! < last.hi));
    // THE OWNER CUE (census, PR #184): where the rig painted this tube to the
    // pelvis, flesh the THIGHS own inside it is leg caught at the junction —
    // the leg is its own tube, parented at the hip joint. Dropped, keeping the
    // piece that holds the tip. A tube painted to the legs (native Tripo, the
    // tiger warrior) is left to geometry alone.
    let legTrimmed = 0;
    if (input.skin) {
      let pelvisOwned = 0;
      for (const u of tube) if (pelvisIdx.has(dominant(u))) pelvisOwned++;
      if (pelvisOwned * 2 >= tube.size) {
        for (const u of [...tube]) {
          if (legIdx.has(dominant(u))) {
            tube.delete(u);
            legTrimmed++;
          }
        }
        if (legTrimmed > 0) {
          const keep = pieces(mesh, tube).find((piece) => piece.includes(tip)) ?? pieces(mesh, tube)[0] ?? [];
          legTrimmed += tube.size - keep.length;
          tube.clear();
          for (const u of keep) tube.add(u);
        }
      }
    }
    const walked = new Set(tube);
    // Wider: rings of faces toward the body (the flyout's ±1). Tighter
    // (negative): rings SHED from the body side — the tube's vertices that
    // touch flesh outside it, which on a walked tube is the junction and
    // never the tip — one ring per step, the piece holding the tip kept. It
    // stops before the tail is gone: a shed that would leave less than
    // TIGHTEST_KEEP of the walked tube is not taken (`rings` says how many were).
    let rings = 0;
    const walkedSize = walked.size;
    for (let ring = 0; ring > growRings; ring--) {
      const edge = [...tube].filter((u) => u !== tip && (adj.get(u) ?? []).some((v) => !tube.has(v)));
      if (edge.length === 0) break;
      const next = new Set(tube);
      for (const u of edge) next.delete(u);
      const keep = pieces(mesh, next).find((piece) => piece.includes(tip)) ?? [];
      if (keep.length < TIGHTEST_KEEP * walkedSize) break;
      tube.clear();
      for (const u of keep) tube.add(u);
      rings--;
    }
    // EXTEND TIPS: the cluster beyond the seam, flooded from the tube through
    // flesh no nearer the body than the tube's own shallowest vertex — a fan's
    // other tails, never the hips.
    // THE SEAM: the tube's shallowest point, as the cluster measures distance
    // from the body. Tips take flesh at or beyond it; the stem, flesh nearer.
    let seam = Infinity;
    for (const u of tube) {
      const d = fromBody.get(u);
      if (d !== undefined) seam = Math.min(seam, d);
    }
    let tipsTaken = 0;
    if (options.extendTips === true) {
      const queue = [...tube];
      while (queue.length > 0) {
        const u = queue.pop()!;
        for (const v of adj.get(u) ?? []) {
          if (tube.has(v) || !cluster.has(v)) continue;
          const d = fromBody.get(v);
          if (d === undefined || d < seam) continue;
          tube.add(v);
          tipsTaken++;
          queue.push(v);
        }
      }
    }
    // EXTEND STEM (the flyout's wider): rings of faces toward the BODY only —
    // flesh the cluster measures nearer the body than the seam, or outside the
    // cluster (the body itself). A fan's sibling sits at or beyond the seam and
    // is left to EXTEND TIPS, so the two never take the same face.
    const towardBody = (v: number): boolean => {
      const dv = fromBody.get(v);
      return dv === undefined || dv < seam;
    };
    let edgeFront = [...tube];
    for (let ring = 0; ring < growRings; ring++) {
      rings++;
      const next: number[] = [];
      for (const u of edgeFront) {
        for (const v of adj.get(u) ?? []) {
          if (!tube.has(v) && towardBody(v)) {
            tube.add(v);
            next.push(v);
          }
        }
      }
      edgeFront = next;
    }
    const vertexList: number[] = [];
    for (const r of tube) for (const t of twins.get(r) ?? [r]) vertexList.push(t);
    vertexList.sort((a, b) => a - b);
    const inTube = new Uint8Array(n);
    for (const v of vertexList) inTube[v] = 1;
    const faces: number[] = [];
    for (let f = 0; f < faceCount; f++) {
      if (inTube[input.indices[f * 3]!] && inTube[input.indices[f * 3 + 1]!] && inTube[input.indices[f * 3 + 2]!]) faces.push(f);
    }
    const radii = accepted.map((s) => s.radius);
    const sortedR = [...radii].sort((a, b) => a - b);
    const medianRadius = sortedR[Math.floor(sortedR.length / 2)]!;
    const sortedRound = accepted.map((s) => s.roundness).sort((a, b) => a - b);
    const roundness = sortedRound[Math.floor(sortedRound.length / 2)]!;
    // A FLATTENED tube (a paddle): most sections close round their centre,
    // one piece, at a bounded aspect. Its thin axis is recorded — a paddle
    // bends across it (up and down), not along it (for the springs lane).
    const closedShare = accepted.filter((q) => q.closed).length / accepted.length;
    let flat: TailCandidate['flat'] = null;
    if (roundness < TAIL_DETECT_LIMITS.minRoundness && closedShare >= TAIL_DETECT_LIMITS.paddleClosed && roundness >= TAIL_DETECT_LIMITS.minPaddleRoundness) {
      let tx = 0;
      let ty = 0;
      let tz = 0;
      let ref: Point3 | null = null;
      for (const q of accepted) {
        if (q.thin === null || !q.closed) continue;
        ref ??= q.thin;
        const sg = q.thin.x * ref.x + q.thin.y * ref.y + q.thin.z * ref.z < 0 ? -1 : 1;
        tx += sg * q.thin.x;
        ty += sg * q.thin.y;
        tz += sg * q.thin.z;
      }
      const tl = Math.hypot(tx, ty, tz) || 1;
      // Pointing up by convention (the axis, not a direction).
      const up = ty < 0 ? -1 : 1;
      flat = { aspect: 1 / roundness, thinAxis: { x: (up * tx) / tl, y: (up * ty) / tl, z: (up * tz) / tl } };
    }
    // The walked length: the farthest tube flesh from the tip (a slice's upper
    // edge would overshoot by up to a slice).
    let length = 0;
    for (const u of reached) {
      const d = fromTip.get(u)!;
      if (d < last.hi && d > length) length = d;
    }
    const root = last.centre;
    const rootDistance = Math.hypot(root.x - C.x, root.y - C.y, root.z - C.z) / height;
    const rootForward = ((root.x - C.x) * F.x + (root.z - C.z) * F.z) / height;
    const slenderness = medianRadius > 0 ? length / medianRadius : Infinity;
    const dominance = new Map<number, number>();
    for (const v of vertexList) {
      const b = dominant(v);
      if (b >= 0) dominance.set(b, (dominance.get(b) ?? 0) + 1);
    }
    const dominantBones = [...dominance.entries()]
      .sort((p, q) => q[1] - p[1])
      .slice(0, 3)
      .map(([b, c]) => ({ bone: input.bones[b]?.name ?? `#${b}`, share: c / vertexList.length }));
    const pct = (x: number): string => `${(100 * x).toFixed(0)}%`;
    const L = TAIL_DETECT_LIMITS;
    let refusedBy: TailGate | null = null;
    let reason: string | null = null;
    if (rootDistance > L.anchorRadius) {
      refusedBy = 'anchor';
      reason = `it meets the body ${pct(rootDistance)} of the height from the hips — a tail meets the body at the pelvis (within ${pct(L.anchorRadius)})`;
    } else if (rootForward >= 0) {
      refusedBy = 'back';
      reason = `it meets the body ${(rootForward * height * 100).toFixed(1)} cm in FRONT of the hips — a tail leaves the back`;
    } else if (length < L.minLength * height) {
      refusedBy = 'length';
      reason = `it is ${(length * 100).toFixed(1)} cm long, ${pct(length / height)} of the height — a tail runs at least ${pct(L.minLength)}`;
    } else if (slenderness < (necked || fan > 1 ? L.minBushySlenderness : L.minSlenderness)) {
      refusedBy = 'slender';
      reason = necked || fan > 1
        ? `it is only ${slenderness.toFixed(1)}× as long as it is thick — even a bushy tail (or a fan's, walked into the others' base) runs at least ${L.minBushySlenderness}×`
        : `it is only ${slenderness.toFixed(1)}× as long as it is thick — a tail is a tube (at least ${L.minSlenderness}×)`;
    } else if (roundness < L.minRoundness && flat === null) {
      refusedBy = 'round';
      reason =
        `its cross-sections read flat (roundness ${roundness.toFixed(2)}, ${(100 * closedShare).toFixed(0)}% of them closed round) — ` +
        `a tube reads ≥ ${L.minRoundness}, a flattened tube (a paddle) closes round at ≥ ${L.minPaddleRoundness} — a panel, not a tail`;
    }
    // A POSSIBLE STUB (Allen, 2026-09-24: "maybe harmless to offer?" —
    // detection proposes, a person decides). Only the cheap, sane filters:
    // refused for being short or fat (never for where it sits), single, near
    // the midline, and sticking OUT behind: the tip farther back than the root
    // by at least its junction's radius, and pointing mostly backward (a lobe
    // of hair or skirt runs down the body, not out of it).
    let kind: 'tube' | 'stub' = 'tube';
    if ((refusedBy === 'length' || refusedBy === 'slender') && fan === 1 && length >= L.minStubLength * height) {
      let lat = 0;
      for (const u of walked) lat += (P[u * 3]! - C.x) * F.z - (P[u * 3 + 2]! - C.z) * F.x;
      lat /= Math.max(1, walked.size);
      const tipP = { x: P[tip * 3]!, z: P[tip * 3 + 2]! };
      const protrusion = (root.x - tipP.x) * F.x + (root.z - tipP.z) * F.z;
      if (Math.abs(lat) <= L.stubMidline * height && protrusion >= last.radius && protrusion >= L.stubOutward * length) kind = 'stub';
    }
    let unarm = 0;
    let pelv = 0;
    for (const s of seedPiece) {
      if (byUnarmatured.has(s)) unarm++;
      if (byPelvis.has(s)) pelv++;
    }
    const c: TailCandidate = {
      vertexIndices: Uint32Array.from(vertexList),
      faceIndices: Uint32Array.from(faces),
      clusterVertices: cluster.size,
      seeds: { unarmatured: unarm, pelvisOwned: pelv },
      tip: { x: P[tip * 3]!, y: P[tip * 3 + 1]!, z: P[tip * 3 + 2]! },
      root,
      length,
      medianRadius,
      slenderness,
      roundness,
      rootDistance,
      rootForward,
      radii,
      legTrimmed,
      rings,
      tipsTaken,
      stop,
      dominantBones,
      clusterRings,
      bushy: necked,
      fan,
      kind,
      flat,
      centring: { ...centringOf(root, tube), yawDeg: tailCentringYaw(root, accepted[Math.round((accepted.length - 1) * 0.7)]!.centre, F) },
      refusedBy,
      reason,
    };
    return { c, walked };
  };
  /**
   * How far a tail sits off the body's mid-sagittal plane: the direction from
   * its root to its flesh's centroid, measured against the plane the rig's up
   * and forward span. The illustrator's sideways sweep (the wolf) reads tens
   * of degrees; a tail hanging centred behind reads a few.
   */
  const centringOf = (root: Point3, tube: ReadonlySet<number>): Omit<TailCentring, 'yawDeg'> => {
    let x = 0;
    let y = 0;
    let z = 0;
    for (const u of tube) {
      x += P[u * 3]!;
      y += P[u * 3 + 1]!;
      z += P[u * 3 + 2]!;
    }
    const n = Math.max(1, tube.size);
    const dx = x / n - root.x;
    const dy = y / n - root.y;
    const dz = z / n - root.z;
    // Lateral = up × forward: the character's LEFT.
    const lx = F.z;
    const lz = -F.x;
    const lateral = dx * lx + dz * lz;
    const len = Math.hypot(dx, dy, dz);
    const angle = len > 0 ? (Math.asin(Math.min(1, Math.abs(lateral) / len)) * 180) / Math.PI : 0;
    const rootOffset = (root.x - C.x) * lx + (root.z - C.z) * lz;
    return {
      angleDeg: angle,
      side: Math.abs(lateral) < 1e-9 ? 'centre' : lateral > 0 ? 'left' : 'right',
      rootOffset,
    };
  };
  for (const seedPiece of pieces(mesh, seeds)) {
    if (seedPiece.length < minCluster) continue;
    let m = measure(seedPiece, CLUSTER_RINGS);
    // THE SEEDS RAN OUT BEFORE THE TUBE DID. Native Tripo paints a tail to
    // the thigh twists, so only its far half lies beyond the bones' reach
    // (the police fox: 79 seeds, a 18 cm walk of a ~35 cm tail, refused as
    // too short and thick). When the walk ran to the cluster's edge without
    // meeting the body and a gate refused it for length or thickness, the
    // cluster grows toward the body and the tube is walked again — until the
    // walk stops on its own evidence (a split, a balloon, a rebound…).
    for (
      let rings = CLUSTER_RINGS + EXTEND_STEP;
      m.c.stop === 'junction' && (m.c.refusedBy === 'length' || m.c.refusedBy === 'slender') && rings <= CLUSTER_RINGS_MAX;
      rings += EXTEND_STEP
    ) {
      m = measure(seedPiece, rings);
    }
    candidates.push(m.c);
  }
  candidates.sort((a, b) =>
    a.refusedBy === null && b.refusedBy !== null
      ? -1
      : b.refusedBy === null && a.refusedBy !== null
        ? 1
        : a.refusedBy === null
          ? b.length - a.length
          : a.rootDistance - b.rootDistance,
  );
  const tail = candidates[0]?.refusedBy === null ? candidates[0] : null;
  const stubOf = tail === null ? candidates.find((c) => c.kind === 'stub') : undefined;
  const stub = stubOf === undefined ? null : { ...stubOf, refusedBy: null, reason: null };
  let summary: string;
  if (tail !== null) {
    summary =
      `tail at the hips: ${(tail.length * 100).toFixed(0)} cm long, ${(tail.medianRadius * 100).toFixed(1)} cm thick, ` +
      `${tail.faceIndices.length} faces, owned today by ${tail.dominantBones.map((d) => `${d.bone} ${(100 * d.share).toFixed(0)}%`).join(', ')}` +
      (tail.fan > 1 ? ` — a FAN: ${tail.fan} tails share this root` : '');
  } else if (candidates.length > 0) {
    const c = candidates[0]!;
    summary = `no tail: the candidate nearest the hips (${c.vertexIndices.length} vertices) was refused by "${c.refusedBy}" — ${c.reason}`;
  } else {
    summary = 'no tail: no flesh behind the hips that no bone reaches, and none the pelvis owns';
  }
  if (stub !== null) {
    summary += ` — a POSSIBLE STUB tail: ${(stub.length * 100).toFixed(0)} cm, ${(stub.medianRadius * 100).toFixed(1)} cm thick, behind the hips on the midline (pin it if it is one)`;
  }
  return { frame, frameRefusal: null, candidates, tail, stub, summary };
}
